import { avisarError } from "../../../utils/avisos";
import React, { useState, useEffect } from "react";
import CartItem from "../../../components/CartItem";
import { Button, Typography, Skeleton, Flex } from "antd";
import { useNavigate } from "react-router-dom";
import { ShoppingOutlined } from "@ant-design/icons";
import { Toaster } from "sonner";
import "./CartView.scss";
import { getProductoPorId } from "../../../utils/productos/productoUtils";
import { getServicioPorId } from "../../../utils/servicios/serviciosUtils";
import { useUser } from "../../../context/UserContext";
import { marcarDisponibilidad } from "../../../utils/carrito/carritoUtils";
import LoginRequired from "../../../components/LoginRequired/LoginRequired";

const { Text, Title, Paragraph } = Typography;

const CartView = ({ onClose, cartItems: cartItemsProp, isDrawer }) => {
  const navigate = useNavigate();
  const { userDataAvailable, cartItems: cartContextItems } = useUser();
  const cartItems = cartItemsProp || cartContextItems;
  const [productoDetalles, setProductoDetalles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determina si se está usando dentro del Drawer o como página completa
  const isDrawerMode = isDrawer !== undefined ? isDrawer : Boolean(onClose);

  const handleCheckout = () => {
    if (typeof onClose === "function") onClose();
    navigate("/checkout");
  };

  const handleContinueShopping = () => {
    if (typeof onClose === "function") onClose();
    navigate("/");
  };

  useEffect(() => {
    const obtenerDetallesTodos = async () => {
      setLoading(true);
      try {
        // Obtener detalles de Productos
        const promisesProductos = (cartItems?.productos || []).map(async (p) => {
          const detalle = await getProductoPorId(p.id);
          return { ...detalle, tipo: "producto", cantidad: p.cantidad };
        });

        // Obtener detalles de Servicios
        const promisesServicios = (cartItems?.servicios || []).map(async (s) => {
          const detalle = await getServicioPorId(s.id);
          return {
            ...detalle,
            tipo: "servicio",
            cantidadDuracion: s.cantidadDuracion,
            fecha: s.fecha,
            hora: s.hora,
          };
        });

        const resultados = await Promise.all([
          ...promisesProductos,
          ...promisesServicios,
        ]);
        setProductoDetalles(await marcarDisponibilidad(resultados.filter((item) => item !== null)));
      } catch (error) {
        console.error("Error al cargar carrito:", error);
        avisarError("No se pudo cargar tu carrito.");
      } finally {
        setLoading(false);
      }
    };

    if (cartItems) {
      obtenerDetallesTodos();
    } else {
      setLoading(false);
    }
  }, [cartItems]);

  const getTotalPrice = (items) => {
    return items.reduce((total, item) => {
      let subtotal = 0;
      if (item.tipo === "producto") {
        subtotal = item.total * item.cantidad;
      } else {
        subtotal =
          item.duracion === "unico"
            ? item.total
            : item.total * item.cantidadDuracion;
      }
      return total + subtotal;
    }, 0);
  };

  // Lo no disponible se muestra pero no suma ni permite pagar
  const disponibles = productoDetalles.filter((item) => !item.noDisponible);
  const hayNoDisponibles = disponibles.length !== productoDetalles.length;
  const total = disponibles.length > 0 ? getTotalPrice(disponibles) : 0;
  const totalItemsCount = disponibles.length;

  if (userDataAvailable === false) {
    return <LoginRequired title="Carrito" subtitle="Inicia sesión para ver los productos y servicios en tu carrito." />;
  }

  // ESTADO DE CARGA (SKELETON)
  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        <Skeleton active paragraph={{ rows: 3 }} />
        <Skeleton
          active
          avatar
          paragraph={{ rows: 2 }}
          style={{ marginTop: 24 }}
        />
        <Skeleton
          active
          avatar
          paragraph={{ rows: 2 }}
          style={{ marginTop: 24 }}
        />
      </div>
    );
  }

  // ESTADO VACÍO
  if (productoDetalles.length === 0) {
    return (
      <Flex 
        vertical 
        justify="center" 
        align="center" 
        style={{ 
          minHeight: isDrawerMode ? "60vh" : "70vh", 
          padding: "40px 20px",
          textAlign: "center" 
        }}
      >
        <div style={{
          width: "100px",
          height: "100px",
          background: "#f0fdf4",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "24px"
        }}>
          <ShoppingOutlined style={{ color: "#1dbf73", fontSize: "40px" }} />
        </div>

        <Title level={3} style={{ 
          marginBottom: "10px", 
          fontWeight: "700",
          color: "#1e293b"
        }}>
          Tu carrito está vacío
        </Title>

        <Paragraph style={{ 
          color: "#64748b", 
          fontSize: "15px", 
          maxWidth: "320px", 
          margin: "0 auto 24px",
          lineHeight: "1.5"
        }}>
          Parece que aún no has agregado nada. <br />
          ¡Explora productos y servicios cerca de ti!
        </Paragraph>

        <Button 
          type="primary" 
          size="large" 
          onClick={handleContinueShopping}
          style={{ 
            backgroundColor: "#1dbf73", 
            borderColor: "#1dbf73",
            height: "48px",
            padding: "0 36px",
            borderRadius: "12px",
            fontWeight: "600",
            fontSize: "15px",
            boxShadow: "0 8px 16px -4px rgba(29, 191, 115, 0.4)"
          }}
        >
          Descubrir productos
        </Button>
      </Flex>
    );
  }

  // MODO DRAWER (Slide-over)
  if (isDrawerMode) {
    return (
      <div className="cart-view-drawer-wrapper">
        <Toaster richColors position="top-center" />

        {/* Lista de Items con Scroll */}
        <div className="cart-items-scroll">
          {productoDetalles.map((item) => (
            <CartItem key={`${item.id}-${item.tipo}`} item={item} />
          ))}
        </div>

        {/* Footer fijo con Subtotal y Botón de Pago */}
        <div className="cart-drawer-footer">
          <div className="summary-row">
            <Text className="summary-label">
              Subtotal ({totalItemsCount} {totalItemsCount === 1 ? "artículo" : "artículos"}):
            </Text>
            <Text strong className="total-amount">
              ${total.toFixed(2)} MXN
            </Text>
          </div>
          <Button
            type="primary"
            size="large"
            block
            className="btn-checkout"
            onClick={handleCheckout}
            disabled={hayNoDisponibles || disponibles.length === 0}
          >
            Ir a finalizar pedido
          </Button>
          <Button
            type="text"
            block
            className="btn-continue"
            onClick={handleContinueShopping}
          >
            Continuar comprando
          </Button>
        </div>
      </div>
    );
  }

  // MODO PÁGINA COMPLETA (/carrito)
  return (
    <div className="cart-page-wrapper">
      <Toaster richColors position="top-center" />

      <h1 className="cart-page-title">
        Carrito de compras ({totalItemsCount} {totalItemsCount === 1 ? "artículo" : "artículos"})
      </h1>

      <div className="cart-page-grid">
        {/* Columna Izquierda: Lista de Items */}
        <div className="cart-items-column">
          {productoDetalles.map((item) => (
            <CartItem key={`${item.id}-${item.tipo}`} item={item} />
          ))}
        </div>

        {/* Columna Derecha: Tarjeta de Resumen */}
        <div className="cart-summary-column">
          <div className="summary-card">
            <h3>Resumen del pedido</h3>
            <div className="summary-row">
              <Text type="secondary">Subtotal</Text>
              <Text strong className="total-amount">${total.toFixed(2)} MXN</Text>
            </div>
            <Button
              type="primary"
              size="large"
              block
              className="btn-checkout"
              onClick={handleCheckout}
              disabled={hayNoDisponibles || disponibles.length === 0}
            >
              Ir a finalizar pedido
            </Button>
            <Button
              type="text"
              block
              style={{ marginTop: 8, color: "#64748b" }}
              onClick={handleContinueShopping}
            >
              Continuar comprando
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartView;
